import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import {
  createPaperAccount, getPortfolio,
  placeOrder, listOrders, cancelOrder,
  getPositions, getPortfolioHistory,
  getQuotes,
  getWatchlist, addToWatchlist, removeFromWatchlist,
  getRiskRules, updateRiskRules,
  createToolPaymentOrder, verifyToolPayment,
} from '../services/papertrading';
import { listStocks } from '../services/marketdata';
import StockChart from '../components/StockChart';
import useMarketStream from '../hooks/useMarketStream';
import { SunIcon as PtSunIcon, MoonIcon as PtMoonIcon } from '../components/icons';
import { formatNumber as fmt, formatCurrency as fmtCurrency } from '../utils/format';
import logo from '../assets/logo-main.png';
import './PaperTrading.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const TOOL_ID           = 'papertrading';
// Market data is fetched server-side every ~1 min, so 1 min is the default
// (and minimum useful) auto-refresh interval on the page.
const DEFAULT_REFRESH_MS = 30000;
const REFRESH_OPTIONS    = [
  [30000,  '30s'],
  [60000,  '60s'],
  [90000,  '90s'],
  [120000, '120s'],
];

// Bottom panel (positions / orders / P&L) is vertically resizable. It defaults
// to 20% of the viewport height and can be dragged up/down via the handle,
// clamped between BOTTOM_MIN_PX and BOTTOM_MAX_RATIO of the viewport. The chosen
// height is persisted locally so it survives reloads.
const BOTTOM_MIN_PX        = 80;
const BOTTOM_MAX_RATIO     = 0.7;
const BOTTOM_DEFAULT_RATIO = 0.2;
const BOTTOM_STORAGE_KEY   = 'pt_bottom_height';

// Side panels are drag-resizable like the bottom panel, and persisted the same
// way. Bounds are absolute pixels rather than viewport ratios: these panels hold
// fixed-width content (prices, an order form), so what matters is that the text
// fits, not what share of the screen they take.
const SIDE_MIN_PX          = 120;
const SIDE_MAX_PX          = 420;
// 240px fits the widest watchlist row comfortably: a 10-character ticker
// (~55px), the change column (44px), the price column (52px), the remove button,
// three gaps and the row padding come to roughly 205px.
const LEFT_DEFAULT_PX      = 240;
const RIGHT_DEFAULT_PX     = 200;
// Keys are versioned because the width is persisted on mount, not only after a
// drag — so every existing visitor already has the previous 160px default
// stored, and a bare default change would never reach them. Bumping the key
// retires those values. Anyone who had deliberately dragged the panel loses that
// choice once, which is the right trade for a default that was too narrow to
// show the content it was sized for.
const LEFT_STORAGE_KEY     = 'pt_left_width_v2';
const RIGHT_STORAGE_KEY    = 'pt_right_width_v2';
function defaultBottomHeight() {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  return Math.round(vh * BOTTOM_DEFAULT_RATIO);
}

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true; s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load Razorpay'));
    document.body.appendChild(s);
  });
}

// NSE regular session: Mon–Fri, 09:15–15:30 IST.
const MARKET_HOURS_LABEL = 'Mon–Fri, 9:15 AM–3:30 PM IST';
function isMarketOpenIST() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find(p => p.type === t)?.value;
  if (['Sat', 'Sun'].includes(get('weekday'))) return false;
  const mins = (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10);
  return mins >= (9 * 60 + 15) && mins <= (15 * 60 + 30);
}

// ── Paywall ───────────────────────────────────────────────────────────────────

function Paywall({ token, refreshUser, theme, setTheme }) {
  const [paying, setPaying] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleSubscribe = async () => {
    setPaying(true); setMsg('');
    try {
      await loadRazorpay();
      const res = await createToolPaymentOrder(token, TOOL_ID);
      if (!res.data?.success) throw new Error(res.data?.message || 'Could not create order');
      const d = res.data.data;
      const rzp = new window.Razorpay({
        key: d.keyId, amount: String(d.amount), currency: d.currency,
        name: d.name, description: d.description, order_id: d.orderId,
        prefill: d.prefill || {}, theme: { color: '#00D4AA' },
        handler: async (r) => {
          try {
            await verifyToolPayment(token, {
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_order_id:   r.razorpay_order_id,
              razorpay_signature:  r.razorpay_signature,
            });
            await refreshUser();
            setMsg('Payment successful! Loading your trading platform…');
          } catch (err) {
            setMsg(err.response?.data?.message || 'Could not verify payment.');
          }
        },
      });
      rzp.on('payment.failed', (r) => setMsg(r.error?.description || 'Payment failed.'));
      rzp.open();
    } catch (err) {
      setMsg(err.response?.data?.message || err.message || 'Could not start payment.');
    } finally { setPaying(false); }
  };

  return (
    <div className="pt-paywall">
      {setTheme && (
        <button
          className="pt-theme-toggle pt-paywall-theme"
          onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
          title="Toggle theme"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <PtSunIcon /> : <PtMoonIcon />}
        </button>
      )}
      <div className="pt-paywall-card">
        <div className="pt-paywall-icon">📈</div>
        <h1 className="pt-paywall-title">Paper Trading</h1>
        <p className="pt-paywall-sub">
          Simulate real NSE trades with <strong>₹10,00,000</strong> virtual capital.
          Live prices, full order book, positions &amp; P&amp;L tracking.
        </p>
        <ul className="pt-paywall-features">
          <li>✓ Live NSE quotes powered by market data</li>
          <li>✓ Place BUY / SELL orders instantly</li>
          <li>✓ Real-time P&amp;L on open positions</li>
          <li>✓ Full order history &amp; portfolio analytics</li>
        </ul>
        <div className="pt-paywall-price">
          <span className="pt-paywall-amount">₹5,000</span>
          <span className="pt-paywall-period">/ month</span>
        </div>
        <button className="pt-paywall-btn" onClick={handleSubscribe} disabled={paying}>
          {paying ? 'Processing…' : 'Subscribe & Start Trading'}
        </button>
        {msg && <p className="pt-paywall-msg">{msg}</p>}
        <p className="pt-paywall-note">One-time payment · Valid for 30 days · Auto-renew not enabled</p>
      </div>
    </div>
  );
}

// ── Order Form ────────────────────────────────────────────────────────────────

function OrderForm({ symbol, quote, account, riskRules, locked, token, onOrderPlaced }) {
  const [side, setSide]       = useState('BUY');
  const [qty,  setQty]        = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg,  setMsg]        = useState(null);

  const ltp        = parseFloat(quote?.ltp)  || 0;
  const qtyNum     = parseInt(qty) || 0;
  const stopNum    = parseFloat(stopLoss) || 0;
  const estValue   = ltp * qtyNum;
  const cashBalance = parseFloat(account?.cash_balance) || 0;
  const isBuy      = side === 'BUY';

  // Find held quantity for SELL
  const heldQty = (account?.positions || []).find(p => p.symbol === symbol)?.quantity || 0;

  // ── Risk context (the user's limits + server-provided live session stats) ──
  const live         = riskRules?.live || {};
  const equity       = parseFloat(live.equity) || parseFloat(account?.total_value) || cashBalance;
  const perTradePct  = parseFloat(riskRules?.max_risk_per_trade_pct) || 0;
  const accountPct   = parseFloat(riskRules?.max_account_risk_pct) || 0;
  const maxTrades    = parseInt(riskRules?.max_trades_per_day, 10) || 0;
  const maxDailyLoss = parseFloat(riskRules?.max_daily_loss_limit) || 0;
  const tradesToday  = parseInt(live.trades_today, 10) || 0;
  const sessionPnl   = parseFloat(live.session_pnl) || 0;
  const openRisk     = parseFloat(live.open_risk_total) || 0;

  const tradeRisk   = isBuy && stopNum > 0 && stopNum < ltp ? (ltp - stopNum) * qtyNum : 0;
  const perTradeCap = equity * (perTradePct / 100);
  const accountCap  = equity * (accountPct / 100);
  const setStopPct  = (pct) => { if (ltp > 0) setStopLoss((ltp * (1 - pct / 100)).toFixed(2)); };

  // ── Front-end validation (mirrors the server gate for instant feedback) ──
  const marketOpen       = isMarketOpenIST();
  const insufficientCash = isBuy  && qtyNum > 0 && estValue > cashBalance;
  const insufficientQty  = !isBuy && qtyNum > 0 && qtyNum > heldQty;
  // Risk gates apply to BUY entries only (SELL exits always reduce risk).
  const tradeLimitHit    = isBuy && maxTrades > 0 && tradesToday >= maxTrades;
  const dailyLossHit     = isBuy && maxDailyLoss > 0 && sessionPnl <= -maxDailyLoss;
  const missingStop      = isBuy && qtyNum > 0 && !(stopNum > 0);
  const stopNotBelow     = isBuy && stopNum > 0 && stopNum >= ltp;
  const perTradeExceeded = isBuy && tradeRisk > 0 && perTradeCap > 0 && tradeRisk > perTradeCap;
  const accountExceeded  = isBuy && tradeRisk > 0 && accountCap > 0 && (openRisk + tradeRisk) > accountCap;

  const canSubmit = !!symbol && qtyNum > 0 && marketOpen && !locked &&
    !insufficientCash && !insufficientQty &&
    (!isBuy || (!tradeLimitHit && !dailyLossHit && !missingStop && !stopNotBelow && !perTradeExceeded && !accountExceeded));

  const blockReason =
    locked             ? 'Account is locked — new entries are blocked.' :
    !marketOpen        ? `Market is closed — trading is open ${MARKET_HOURS_LABEL}.` :
    insufficientCash   ? `Insufficient cash. Required: ${fmtCurrency(estValue)}, Available: ${fmtCurrency(cashBalance)}` :
    insufficientQty    ? `Insufficient holdings. You hold ${heldQty} share${heldQty === 1 ? '' : 's'}.` :
    tradeLimitHit      ? `Daily trade limit reached (${tradesToday}/${maxTrades}).` :
    dailyLossHit       ? `Daily loss limit reached (P&L ${fmtCurrency(sessionPnl)}).` :
    missingStop        ? 'Set a protective stop-loss price for this BUY.' :
    stopNotBelow       ? `Stop-loss must be below the market price (${fmtCurrency(ltp)}).` :
    perTradeExceeded   ? `Order risk ${fmtCurrency(tradeRisk)} exceeds your per-trade limit ${fmtCurrency(perTradeCap)} (${perTradePct}% of equity).` :
    accountExceeded    ? `This would breach your account risk limit ${fmtCurrency(accountCap)} (${accountPct}% of equity).` :
    null;

  const handlePlace = async () => {
    if (!symbol || !qty || parseInt(qty) <= 0) { setMsg({ type: 'error', text: 'Enter a valid quantity.' }); return; }
    setLoading(true); setMsg(null);
    try {
      const payload = { symbol, side, quantity: parseInt(qty), order_type: 'MARKET' };
      if (isBuy) payload.stop_loss_price = stopNum;
      const res = await placeOrder(token, payload);
      setMsg({ type: 'success', text: res.data.data?.message || 'Order placed successfully.' });
      setQty(''); setStopLoss('');
      onOrderPlaced();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Order failed.' });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-order-panel">
      <div className="pt-order-symbol">{symbol || '—'}</div>

      <div className="pt-order-side-toggle">
        <button
          className={`pt-side-btn buy${side === 'BUY' ? ' active' : ''}`}
          onClick={() => setSide('BUY')}
        >BUY</button>
        <button
          className={`pt-side-btn sell${side === 'SELL' ? ' active' : ''}`}
          onClick={() => setSide('SELL')}
        >SELL</button>
      </div>

      <div className={`pt-order-fields-row${isBuy ? '' : ' single'}`}>
        <div className="pt-order-field">
          <label>Quantity</label>
          <input
            type="number" min="1" step="1"
            value={qty}
            onChange={e => setQty(e.target.value)}
            placeholder="0"
            className="pt-order-input"
          />
        </div>

        {isBuy && (
          <div className="pt-order-field">
            <label>Stop Loss (₹) *</label>
            <input
              type="number" min="0" step="0.05"
              value={stopLoss}
              onChange={e => setStopLoss(e.target.value)}
              placeholder="0.00"
              className="pt-order-input"
            />
          </div>
        )}
      </div>

      {isBuy && (
        <div className="pt-sl-presets">
          {[2, 5, 10].map(p => (
            <button
              key={p} type="button" className="pt-sl-preset"
              onClick={() => setStopPct(p)} disabled={!(ltp > 0)}
              title={`Set stop ${p}% below market price`}
            >-{p}%</button>
          ))}
        </div>
      )}

      <div className="pt-order-summary">
        <div className="pt-order-summary-row"><span>Order Type</span><strong>MARKET</strong></div>
        <div className="pt-order-summary-row"><span>Market Price</span><strong>{fmtCurrency(ltp)}</strong></div>
        <div className="pt-order-summary-row">
          <span>Est. Value</span>
          <strong className="highlight">{estValue > 0 ? fmtCurrency(estValue) : '—'}</strong>
        </div>
        {isBuy ? (
          <div className="pt-order-summary-row"><span>Available Cash</span><strong>{fmtCurrency(cashBalance)}</strong></div>
        ) : (
          <div className="pt-order-summary-row"><span>Held Quantity</span><strong>{heldQty} shares</strong></div>
        )}
        {isBuy && tradeRisk > 0 && (
          <div className="pt-order-summary-row">
            <span>Trade Risk{perTradeCap > 0 ? ` · limit ${fmtCurrency(perTradeCap)}` : ''}</span>
            <strong className={perTradeExceeded ? 'down' : 'up'}>{fmtCurrency(tradeRisk)}</strong>
          </div>
        )}
      </div>

      <button
        className={`pt-order-submit ${side.toLowerCase()}`}
        onClick={handlePlace}
        disabled={loading || !canSubmit}
      >
        {loading ? 'Placing…' : locked ? 'Account Locked' : !marketOpen ? 'Market Closed' : `Place ${side} Order`}
      </button>

      {blockReason && !loading && (
        <div className="pt-order-msg error">{blockReason}</div>
      )}
      {msg && (
        <div className={`pt-order-msg ${msg.type}`}>{msg.text}</div>
      )}
    </div>
  );
}

// ── Quote Panel ───────────────────────────────────────────────────────────────

function QuotePanel({ symbol, quote }) {
  if (!quote) {
    return (
      <div className="pt-quote-panel empty">
        <div className="pt-quote-empty">Select a stock from the watchlist</div>
      </div>
    );
  }

  const change    = parseFloat(quote.change)     || 0;
  const changePct = parseFloat(quote.change_pct) || 0;
  const isUp      = change >= 0;

  return (
    <div className="pt-quote-panel">
      <div className="pt-quote-header">
        <div>
          <div className="pt-quote-symbol">{symbol}</div>
          <div className="pt-quote-exchange">NSE</div>
        </div>
        <div className="pt-quote-right">
          <div className="pt-quote-ltp">{fmtCurrency(quote.ltp)}</div>
          <div className={`pt-quote-change ${isUp ? 'up' : 'down'}`}>
            {isUp ? '▲' : '▼'} {fmt(Math.abs(change))} ({fmt(Math.abs(changePct))}%)
          </div>
        </div>
      </div>

      <div className="pt-quote-grid">
        <div className="pt-quote-item"><span>Open</span><strong>{fmtCurrency(quote.open)}</strong></div>
        <div className="pt-quote-item"><span>High</span><strong>{fmtCurrency(quote.high)}</strong></div>
        <div className="pt-quote-item"><span>Low</span><strong>{fmtCurrency(quote.low)}</strong></div>
        <div className="pt-quote-item"><span>Prev Close</span><strong>{fmtCurrency(quote.prev_close)}</strong></div>
        <div className="pt-quote-item"><span>Volume</span><strong>{Number(quote.volume || 0).toLocaleString('en-IN')}</strong></div>
        <div className="pt-quote-item"><span>52W High</span><strong>{fmtCurrency(quote.week_52_high)}</strong></div>
        <div className="pt-quote-item"><span>52W Low</span><strong>{fmtCurrency(quote.week_52_low)}</strong></div>
        {quote.market_cap != null && (
          <div className="pt-quote-item"><span>Mkt Cap</span><strong>{fmtCurrency(quote.market_cap)}</strong></div>
        )}
      </div>
    </div>
  );
}

// ── Stock Browser ─────────────────────────────────────────────────────────────

function StockBrowser({ token, watchlist, onAdd, onClose }) {
  const [search,  setSearch]  = useState('');
  const [stocks,  setStocks]  = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchStocks = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await listStocks(token, q);
      if (res.data?.success) setStocks(res.data.data.stocks || []);
    } catch { setStocks([]); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStocks(''); }, [fetchStocks]);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchStocks(q.trim().toUpperCase()), 300);
  };

  return (
    <div className="pt-browser-overlay" onClick={onClose}>
      <div className="pt-browser" onClick={e => e.stopPropagation()}>
        <div className="pt-browser-header">
          <span>Browse Stocks</span>
          <button className="pt-browser-close" onClick={onClose}>✕</button>
        </div>
        <div className="pt-browser-search">
          <input
            autoFocus
            value={search}
            onChange={handleSearch}
            placeholder="Search symbol or company…"
            className="pt-search-input"
          />
        </div>
        <div className="pt-browser-list">
          {loading && <div className="pt-browser-msg">Loading…</div>}
          {!loading && stocks.length === 0 && <div className="pt-browser-msg">No stocks found.</div>}
          {stocks.map(s => {
            const inList = watchlist.includes(s.symbol);
            return (
              <div key={s.symbol} className="pt-browser-item">
                <div className="pt-browser-left">
                  <div className="pt-browser-sym">{s.symbol}</div>
                  <div className="pt-browser-co">{s.company_name}</div>
                </div>
                <div className="pt-browser-right">
                  {s.sector && <span className="pt-browser-sector">{s.sector}</span>}
                  <button
                    className={`pt-browser-add${inList ? ' added' : ''}`}
                    disabled={inList}
                    onClick={() => { if (!inList) { onAdd(s.symbol); } }}
                  >
                    {inList ? '✓' : '+'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Watchlist ─────────────────────────────────────────────────────────────────

function Watchlist({ token, watchlist, selected, quotes, onSelect, onAdd, onRemove }) {
  const [showBrowser, setShowBrowser] = useState(false);

  return (
    <div className="pt-watchlist">
      <div className="pt-watchlist-header">
        <span>Watchlist</span>
        <button className="pt-wl-browse-btn" onClick={() => setShowBrowser(true)} title="Browse stocks">+ Browse</button>
      </div>

      <div className="pt-watchlist-items">
        {watchlist.length === 0 ? (
          <div className="pt-wl-empty">
            <p>No stocks added yet.</p>
            <button className="pt-wl-browse-cta" onClick={() => setShowBrowser(true)}>Browse &amp; Add Stocks</button>
          </div>
        ) : watchlist.map(sym => {
          const q      = quotes[sym];
          const change = parseFloat(q?.change_pct) || 0;
          const isUp   = change >= 0;
          return (
            <div
              key={sym}
              className={`pt-wl-item${selected === sym ? ' selected' : ''}`}
              onClick={() => onSelect(sym)}
            >
              {/* Single row: symbol · change% · price · remove. Previously the
                  symbol and price were stacked, which made each entry two lines
                  tall and the panel taller than it needed to be. */}
              <div className="pt-wl-sym">{sym}</div>
              <div className={`pt-wl-change ${isUp ? 'up' : 'down'}`}>
                {isUp ? '+' : ''}{fmt(change)}%
              </div>
              <div className="pt-wl-ltp">{q ? fmtCurrency(q.ltp) : '…'}</div>
              <button
                className="pt-wl-remove"
                onClick={e => { e.stopPropagation(); onRemove(sym); }}
                aria-label={`Remove ${sym}`}
              >×</button>
            </div>
          );
        })}
      </div>

      {showBrowser && (
        <StockBrowser
          token={token}
          watchlist={watchlist}
          onAdd={(sym) => { onAdd(sym); }}
          onClose={() => setShowBrowser(false)}
        />
      )}
    </div>
  );
}

// ── Bottom Tabs ───────────────────────────────────────────────────────────────

function PositionsTab({ positions }) {
  if (!positions.length) {
    return <div className="pt-empty-tab">No open positions. Place your first trade!</div>;
  }
  return (
    <div className="pt-table-wrap">
      <table className="pt-table">
        <thead>
          <tr>
            <th>Symbol</th><th>Qty</th><th>Avg Buy</th><th>Stop Loss</th>
            <th>CMP</th><th>P&amp;L</th><th>P&amp;L %</th><th>Value</th>
          </tr>
        </thead>
        <tbody>
          {positions.map(p => (
            <tr key={p.symbol}>
              <td className="pt-td-sym">{p.symbol}</td>
              <td>{p.quantity}</td>
              <td>{fmtCurrency(p.avg_buy_price)}</td>
              <td>{p.stop_loss_price != null ? fmtCurrency(p.stop_loss_price) : '—'}</td>
              <td>{fmtCurrency(p.current_price)}</td>
              <td className={p.unrealised_pnl >= 0 ? 'up' : 'down'}>
                {p.unrealised_pnl >= 0 ? '+' : ''}{fmtCurrency(p.unrealised_pnl)}
              </td>
              <td className={p.pnl_pct >= 0 ? 'up' : 'down'}>
                {p.pnl_pct >= 0 ? '+' : ''}{fmt(p.pnl_pct)}%
              </td>
              <td>{fmtCurrency(p.current_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrdersTab({ orders, token, onCancelled }) {
  const [cancelling, setCancelling] = useState(null);

  const handleCancel = async (id) => {
    setCancelling(id);
    try {
      await cancelOrder(token, id);
      onCancelled();
    } catch { /* ignore */ }
    finally { setCancelling(null); }
  };

  if (!orders.length) {
    return <div className="pt-empty-tab">No orders yet.</div>;
  }

  const STATUS_COLOR = { FILLED: 'up', PENDING: 'warn', CANCELLED: 'muted', REJECTED: 'down' };

  return (
    <div className="pt-table-wrap">
      <table className="pt-table">
        <thead>
          <tr>
            <th>Symbol</th><th>Side</th><th>Qty</th>
            <th>Type</th><th>Price</th><th>Value</th><th>Status</th><th>Time</th><th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.id}>
              <td className="pt-td-sym">{o.symbol}</td>
              <td className={o.side === 'BUY' ? 'up' : 'down'}>{o.side}</td>
              <td>{o.quantity}</td>
              <td>{o.order_type}</td>
              <td>{o.filled_price ? fmtCurrency(o.filled_price) : '—'}</td>
              <td>{o.filled_price ? fmtCurrency(o.filled_price * o.quantity) : '—'}</td>
              <td><span className={`pt-badge ${STATUS_COLOR[o.status] || ''}`}>{o.status}</span></td>
              <td>{o.created_at ? new Date(o.created_at).toLocaleTimeString('en-IN') : '—'}</td>
              <td>
                {o.status === 'PENDING' && (
                  <button
                    className="pt-cancel-btn"
                    onClick={() => handleCancel(o.id)}
                    disabled={cancelling === o.id}
                  >
                    {cancelling === o.id ? '…' : 'Cancel'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PnlChart({ history, theme }) {
  if (!history.length) {
    return <div className="pt-empty-tab">No portfolio history yet. Start trading to see P&amp;L chart.</div>;
  }

  const gridStroke = theme === 'light' ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.06)';
  const axisFill   = theme === 'light' ? '#586577' : '#888';

  const data = history.map(h => ({
    date:  new Date(h.ts).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }),
    value: parseFloat(h.total_value) || 0,
  }));

  const startValue = 1000000;
  const lastValue  = data[data.length - 1]?.value || startValue;
  const isUp       = lastValue >= startValue;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="pt-tooltip">
        <div className="pt-tooltip-label">{label}</div>
        <div className="pt-tooltip-value">{fmtCurrency(payload[0].value)}</div>
      </div>
    );
  };

  return (
    <div className="pt-chart-wrap">
      <div className="pt-chart-header">
        <span>Portfolio Value</span>
        <span className={isUp ? 'up' : 'down'}>
          {fmtCurrency(lastValue)} ({isUp ? '+' : ''}{fmt(((lastValue - startValue) / startValue) * 100)}%)
        </span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="ptGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={isUp ? '#00D4AA' : '#FF4D6D'} stopOpacity={0.3} />
              <stop offset="95%" stopColor={isUp ? '#00D4AA' : '#FF4D6D'} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: axisFill }} />
          <YAxis tick={{ fontSize: 11, fill: axisFill }} tickFormatter={v => '₹' + (v / 1000).toFixed(0) + 'K'} />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone" dataKey="value"
            stroke={isUp ? '#00D4AA' : '#FF4D6D'}
            strokeWidth={2}
            fill="url(#ptGrad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Topbar ────────────────────────────────────────────────────────────────────

function PtTopbar({ account, refreshMs, refreshing, lastRefresh, onChangeRefresh, onManualRefresh, onOpenRisk, locked, theme, setTheme }) {
  const totalValue  = parseFloat(account?.total_value)  || 0;
  const startCapital = 1000000;
  const pnl         = totalValue - startCapital;
  const isUp        = pnl >= 0;

  const ctrlStyle = {
    background: 'var(--pt-ctrl-bg)', color: 'var(--pt-text)',
    border: '1px solid var(--pt-ctrl-border)', borderRadius: 6,
    fontSize: 12,
  };

  return (
    <header className="pt-topbar">
      <Link to="/dashboard" className="pt-topbar-logo" title="Back to dashboard">
        <img
          src={logo}
          alt="InvFly"
          style={{ height: 28, filter: theme === 'light' ? 'none' : 'brightness(0) invert(1)' }}
        />
      </Link>
      <div className="pt-topbar-title">Paper Trading</div>
      <div className="pt-topbar-stats">
        <div className="pt-stat">
          <span className="pt-stat-label">Cash</span>
          <span className="pt-stat-value">{fmtCurrency(account?.cash_balance)}</span>
        </div>
        <div className="pt-stat">
          <span className="pt-stat-label">Portfolio</span>
          <span className="pt-stat-value">{fmtCurrency(account?.total_value)}</span>
        </div>
        <div className="pt-stat">
          <span className="pt-stat-label">P&amp;L</span>
          <span className={`pt-stat-value ${isUp ? 'up' : 'down'}`}>
            {isUp ? '+' : ''}{fmtCurrency(pnl)}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {setTheme && (
          <button
            className="pt-theme-toggle"
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
            title="Toggle theme"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <PtSunIcon /> : <PtMoonIcon />}
          </button>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
          <span className="pt-stat-label">Auto-refresh</span>
          {lastRefresh && <span style={{ fontSize: 10, color: 'var(--pt-muted)' }}>Updated {lastRefresh}</span>}
        </div>
        <select
          value={refreshMs}
          onChange={e => onChangeRefresh(Number(e.target.value))}
          style={{ ...ctrlStyle, padding: '6px 8px', cursor: 'pointer' }}
          title="Auto-refresh interval"
        >
          {REFRESH_OPTIONS.map(([ms, label]) => (
            <option key={ms} value={ms} style={{ color: '#111' }}>{label}</option>
          ))}
        </select>
        <button
          className="pt-refresh-btn"
          onClick={onManualRefresh}
          disabled={refreshing}
          title="Refresh now"
        >
          <span className={refreshing ? 'pt-spin' : ''} style={{ display: 'inline-block' }}>↻</span>
          <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
        </button>
        <button
          className={`pt-refresh-btn${locked ? ' pt-risk-locked' : ''}`}
          onClick={onOpenRisk}
          title="Risk settings"
        >
          <span>{locked ? '🔒' : '⚙'}</span>
          <span>Risk</span>
        </button>
      </div>
    </header>
  );
}

// ── Risk Settings Modal ─────────────────────────────────────────────────────────

const ACCOUNT_RISK_OPTIONS = [2, 3, 4, 5, 6];

function RiskConfigModal({ rules, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    max_account_risk_pct:     rules?.max_account_risk_pct ?? 5,
    max_trades_per_day:       rules?.max_trades_per_day ?? 5,
    max_daily_loss_limit:     rules?.max_daily_loss_limit ?? 50000,
    max_position_size:        rules?.max_position_size ?? 0,
    enforce_hard_liquidation: rules?.enforce_hard_liquidation ?? true,
    rule_lock_duration_hours: rules?.rule_lock_duration_hours ?? 24,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const mar  = parseFloat(form.max_account_risk_pct) || 0;
  const mtpd = parseInt(form.max_trades_per_day, 10) || 0;
  // Per-trade risk is derived = Max Account Risk / Max Trades Per Day.
  const perTrade = mtpd > 0 ? (mar / mtpd).toFixed(2) : '—';

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const res = await updateRiskRules(token, {
        max_account_risk_pct:     Number(form.max_account_risk_pct),
        max_trades_per_day:       Number(form.max_trades_per_day),
        max_daily_loss_limit:     Number(form.max_daily_loss_limit),
        max_position_size:        Number(form.max_position_size) || 0,
        enforce_hard_liquidation: !!form.enforce_hard_liquidation,
        rule_lock_duration_hours: Number(form.rule_lock_duration_hours),
      });
      if (res.data?.success) { onSaved(res.data.data); onClose(); }
      else setError(res.data?.message || 'Could not save risk rules.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save risk rules.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pt-risk-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pt-risk-modal" role="dialog" aria-modal="true">
        <div className="pt-risk-header">
          <span>Risk Settings</span>
          <button className="pt-browser-close" onClick={onClose}>✕</button>
        </div>

        <form className="pt-risk-form" onSubmit={handleSave}>
          <div className="pt-risk-field">
            <label>Max Account Risk (%)</label>
            <select
              value={form.max_account_risk_pct}
              onChange={e => set('max_account_risk_pct', e.target.value)}
              className="pt-order-input"
            >
              {ACCOUNT_RISK_OPTIONS.map(p => <option key={p} value={p}>{p}%</option>)}
            </select>
            <span className="pt-risk-hint">Max cumulative loss across open positions before hard-liquidation.</span>
          </div>

          <div className="pt-risk-field">
            <label>Max Trades Per Day</label>
            <input
              type="number" min="1" step="1" className="pt-order-input"
              value={form.max_trades_per_day}
              onChange={e => set('max_trades_per_day', e.target.value)}
            />
          </div>

          <div className="pt-risk-field">
            <label>Max Daily Loss Limit (₹)</label>
            <input
              type="number" min="0" step="100" className="pt-order-input"
              value={form.max_daily_loss_limit}
              onChange={e => set('max_daily_loss_limit', e.target.value)}
            />
            <span className="pt-risk-hint">Realized + floating P&amp;L cap. Reaching it locks new entries.</span>
          </div>

          <div className="pt-risk-field">
            <label>Max Risk Per Trade</label>
            <div className="pt-order-value highlight">{perTrade}% <span className="pt-risk-hint">(derived = MAR ÷ trades/day)</span></div>
          </div>

          <div className="pt-risk-field">
            <label>Max Position Size (shares, 0 = uncapped)</label>
            <input
              type="number" min="0" step="1" className="pt-order-input"
              value={form.max_position_size}
              onChange={e => set('max_position_size', e.target.value)}
            />
          </div>

          <div className="pt-risk-field pt-risk-row">
            <label className="pt-risk-check">
              <input
                type="checkbox"
                checked={!!form.enforce_hard_liquidation}
                onChange={e => set('enforce_hard_liquidation', e.target.checked)}
              />
              Enforce hard liquidation
            </label>
          </div>

          <div className="pt-risk-field">
            <label>Lock Duration (hours)</label>
            <input
              type="number" min="1" step="1" className="pt-order-input"
              value={form.rule_lock_duration_hours}
              onChange={e => set('rule_lock_duration_hours', e.target.value)}
            />
          </div>

          {error && <div className="pt-order-msg error">{error}</div>}

          <div className="pt-risk-actions">
            <button type="button" className="pt-risk-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="pt-risk-save" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const PaperTrading = ({ theme, setTheme }) => {
  const { token, refreshUser, paperTradingAccess } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [quotes,    setQuotes]     = useState({});
  const [account,   setAccount]    = useState(null);
  const [positions, setPositions]  = useState([]);
  const [orders,    setOrders]     = useState([]);
  const [history,   setHistory]    = useState([]);
  const [bottomTab, setBottomTab]  = useState('positions');
  const [refreshMs,   setRefreshMs]   = useState(DEFAULT_REFRESH_MS);
  const [refreshing,  setRefreshing]  = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [riskRules,    setRiskRules]    = useState(null);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const refreshTimer = useRef(null);

  const locked = !!riskRules?.is_locked;

  // ── Resizable bottom panel (positions / orders / P&L) ──────────────────────
  const [leftWidth, setLeftWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem(LEFT_STORAGE_KEY), 10);
    return Number.isFinite(saved) ? Math.min(Math.max(saved, SIDE_MIN_PX), SIDE_MAX_PX) : LEFT_DEFAULT_PX;
  });
  const [rightWidth, setRightWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem(RIGHT_STORAGE_KEY), 10);
    return Number.isFinite(saved) ? Math.min(Math.max(saved, SIDE_MIN_PX), SIDE_MAX_PX) : RIGHT_DEFAULT_PX;
  });

  const [bottomHeight, setBottomHeight] = useState(() => {
    const saved = parseInt(localStorage.getItem(BOTTOM_STORAGE_KEY), 10);
    return Number.isFinite(saved) ? saved : defaultBottomHeight();
  });

  useEffect(() => {
    localStorage.setItem(BOTTOM_STORAGE_KEY, String(Math.round(bottomHeight)));
  }, [bottomHeight]);

  useEffect(() => {
    localStorage.setItem(LEFT_STORAGE_KEY, String(Math.round(leftWidth)));
  }, [leftWidth]);

  useEffect(() => {
    localStorage.setItem(RIGHT_STORAGE_KEY, String(Math.round(rightWidth)));
  }, [rightWidth]);

  /**
   * Drag a side panel.
   *
   * `side` decides which edge the width is measured from: the left panel grows
   * with the pointer's x, the right panel grows as x decreases. Both clamp to
   * the same bounds so neither can be dragged shut or eat the chart.
   */
  const startSideResize = useCallback((side) => (e) => {
    e.preventDefault();
    const setter = side === 'left' ? setLeftWidth : setRightWidth;
    const onMove = (ev) => {
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const raw = side === 'left' ? clientX : window.innerWidth - clientX;
      setter(Math.min(Math.max(raw, SIDE_MIN_PX), SIDE_MAX_PX));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, []);

  const startBottomResize = useCallback((e) => {
    e.preventDefault();
    const onMove = (ev) => {
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const maxH = window.innerHeight * BOTTOM_MAX_RATIO;
      // Height = distance from the pointer to the bottom of the viewport, so
      // dragging the handle up grows the panel and dragging down shrinks it.
      const next = Math.min(Math.max(window.innerHeight - clientY, BOTTOM_MIN_PX), maxH);
      setBottomHeight(next);
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, []);

  // ── Load the user's saved watchlist (starts empty until they Browse & add) ──
  useEffect(() => {
    if (!token) return;
    getWatchlist(token).then(res => {
      if (!res.data?.success) return;
      const syms = res.data.data.symbols || [];
      setWatchlist(syms);
      // Restore the symbol from the URL (?symbol=) on a full refresh, else first
      const urlSym = searchParams.get('symbol');
      setSelected(syms.includes(urlSym) ? urlSym : (syms[0] || null));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Keep the selected symbol in the URL so a full page refresh restores it
  useEffect(() => {
    if (!selected) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('symbol', selected);
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // ── Init account ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !paperTradingAccess?.active) return;
    (async () => {
      try {
        await createPaperAccount(token);
        await refreshData();
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, paperTradingAccess?.active]);


  // ── Auto-refresh: quotes (drives the live chart) + account/positions/P&L ──
  useEffect(() => {
    if (!paperTradingAccess?.active || !watchlist.length) return;
    const tick = () => {
      Promise.all([fetchQuotes(), refreshData()]).then(() => {
        setLastRefresh(new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }));
      });
    };
    tick();
    refreshTimer.current = setInterval(tick, refreshMs);
    return () => clearInterval(refreshTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist, paperTradingAccess?.active, refreshMs]);

  const fetchQuotes = useCallback(async () => {
    if (!watchlist.length) return;
    try {
      const res = await getQuotes(token, watchlist);
      if (res.data?.success) {
        const map = {};
        const rows = res.data.data?.quotes || [];
        rows.forEach(q => { map[q.symbol] = q; });
        setQuotes(map);
      }
    } catch { /* silent */ }
  }, [watchlist, token]);

  // ── Live price overlay ─────────────────────────────────────────────────────
  // Additive on purpose. The 30s poll above stays the source of truth for
  // everything the lite tick stream does not carry (open/high/low, prev_close,
  // volume, 52-week) and remains the sole path when the socket is unavailable.
  // This only patches ltp/change/change_pct, so a socket failure is a non-event.
  const { ticks: liveTicks } = useMarketStream(token, watchlist);

  useEffect(() => {
    const symbols = Object.keys(liveTicks);
    if (symbols.length === 0) return;

    setQuotes(prev => {
      let changed = false;
      const next = { ...prev };
      for (const sym of symbols) {
        const base = prev[sym];
        // Only patch rows the poll has already established: without prev_close
        // we cannot compute change, and a half-populated row would render as
        // dashes across the watchlist.
        if (!base) continue;
        const ltp = Number(liveTicks[sym].ltp);
        if (!Number.isFinite(ltp) || ltp === Number(base.ltp)) continue;

        const prevClose = Number(base.prev_close);
        const change = Number.isFinite(prevClose) ? Number((ltp - prevClose).toFixed(2)) : base.change;
        const changePct = Number.isFinite(prevClose) && prevClose !== 0
          ? Number((((ltp - prevClose) / prevClose) * 100).toFixed(2))
          : base.change_pct;

        next[sym] = { ...base, ltp, change, change_pct: changePct };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [liveTicks]);

  const refreshData = useCallback(async () => {
    try {
      const [acctRes, posRes, ordRes, histRes, riskRes] = await Promise.allSettled([
        getPortfolio(token),
        getPositions(token),
        listOrders(token, { limit: 50 }),
        getPortfolioHistory(token, 200),
        getRiskRules(token),
      ]);
      if (acctRes.status === 'fulfilled' && acctRes.value.data?.success)
        setAccount(acctRes.value.data.data);
      if (posRes.status  === 'fulfilled' && posRes.value.data?.success)
        setPositions(posRes.value.data.data?.positions || []);
      if (ordRes.status  === 'fulfilled' && ordRes.value.data?.success)
        setOrders(ordRes.value.data.data?.orders || []);

      if (histRes.status === 'fulfilled' && histRes.value.data?.success)
        setHistory(histRes.value.data.data?.history || []);
      // Refreshing risk rules each tick surfaces a monitor-triggered lock quickly.
      if (riskRes.status === 'fulfilled' && riskRes.value.data?.success)
        setRiskRules(riskRes.value.data.data);
    } catch { /* silent */ }
  }, [token]);

  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchQuotes(), refreshData()]);
      setLastRefresh(new Date().toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }));
    } finally {
      setRefreshing(false);
    }
  }, [fetchQuotes, refreshData]);

  // Re-sync the watchlist from the server (used to recover from a failed write).
  const resyncWatchlist = useCallback(() => {
    getWatchlist(token)
      .then(r => { if (r.data?.success) setWatchlist(r.data.data.symbols || []); })
      .catch(() => {});
  }, [token]);

  const handleAddSymbol = (sym) => {
    setWatchlist(prev => prev.includes(sym) ? prev : [...prev, sym]); // optimistic
    setSelected(sym);
    addToWatchlist(token, sym)
      .then(res => { if (res.data?.success) setWatchlist(res.data.data.symbols || []); })
      .catch(resyncWatchlist);
  };

  const handleRemoveSymbol = (sym) => {
    setWatchlist(prev => {                                            // optimistic
      const next = prev.filter(s => s !== sym);
      if (selected === sym) setSelected(next[0] || null);
      return next;
    });
    removeFromWatchlist(token, sym)
      .then(res => { if (res.data?.success) setWatchlist(res.data.data.symbols || []); })
      .catch(resyncWatchlist);
  };

  // ── Paywall ──────────────────────────────────────────────────────────────
  if (!paperTradingAccess?.active) {
    return <Paywall token={token} refreshUser={refreshUser} theme={theme} setTheme={setTheme} />;
  }

  return (
    <div className="pt-page">
      <PtTopbar
        account={account}
        refreshMs={refreshMs}
        refreshing={refreshing}
        lastRefresh={lastRefresh}
        onChangeRefresh={setRefreshMs}
        onManualRefresh={handleManualRefresh}
        onOpenRisk={() => setShowRiskModal(true)}
        locked={locked}
        theme={theme}
        setTheme={setTheme}
      />

      {locked && (
        <div className="pt-locked-banner">
          🔒 Trading locked{riskRules?.locked_reason ? ` — ${riskRules.locked_reason}` : ''}. New entries are blocked
          {riskRules?.locked_until ? ` until ${new Date(riskRules.locked_until).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}` : ''}.
        </div>
      )}

      {/* ── Main 3-panel layout ── */}
      {/* Column widths are inline because they are user-dragged state; the
          grid template is `left | handle | chart | handle | right`. */}
      <div
        className="pt-main"
        style={{ gridTemplateColumns: `${leftWidth}px 7px minmax(0, 1fr) 7px ${rightWidth}px` }}
      >
        <Watchlist
          token={token}
          watchlist={watchlist}
          selected={selected}
          quotes={quotes}
          onSelect={setSelected}
          onAdd={handleAddSymbol}
          onRemove={handleRemoveSymbol}
        />

        <div
          className="pt-vresize-handle"
          onMouseDown={startSideResize('left')}
          onTouchStart={startSideResize('left')}
          onDoubleClick={() => setLeftWidth(LEFT_DEFAULT_PX)}
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize · double-click to reset"
        >
          <div className="pt-vresize-grip" />
        </div>

        <StockChart
          symbol={selected}
          quote={quotes[selected]}
          token={token}
          theme={theme}
        />

        <div
          className="pt-vresize-handle"
          onMouseDown={startSideResize('right')}
          onTouchStart={startSideResize('right')}
          onDoubleClick={() => setRightWidth(RIGHT_DEFAULT_PX)}
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize · double-click to reset"
        >
          <div className="pt-vresize-grip" />
        </div>

        <OrderForm
          symbol={selected}
          quote={quotes[selected]}
          account={{ ...account, positions }}
          riskRules={riskRules}
          locked={locked}
          token={token}
          onOrderPlaced={() => { refreshData(); fetchQuotes(); }}
        />
      </div>

      {/* ── Resize handle: drag to size the bottom panel, double-click to reset ── */}
      <div
        className="pt-resize-handle"
        onMouseDown={startBottomResize}
        onTouchStart={startBottomResize}
        onDoubleClick={() => setBottomHeight(defaultBottomHeight())}
        role="separator"
        aria-orientation="horizontal"
        title="Drag to resize · double-click to reset"
      >
        <div className="pt-resize-grip" />
      </div>

      {/* ── Bottom panel ── */}
      <div className="pt-bottom" style={{ height: bottomHeight }}>
        <div className="pt-bottom-tabs">
          {['positions', 'orders', 'pnl'].map(t => (
            <button
              key={t}
              className={`pt-bottom-tab${bottomTab === t ? ' active' : ''}`}
              onClick={() => setBottomTab(t)}
            >
              {t === 'positions' ? `Positions (${positions.length})` :
               t === 'orders'   ? `Orders (${orders.length})` :
               'P&L Chart'}
            </button>
          ))}
        </div>
        <div className="pt-bottom-content">
          {bottomTab === 'positions' && <PositionsTab positions={positions} />}
          {bottomTab === 'orders'    && (
            <OrdersTab orders={orders} token={token} onCancelled={refreshData} />
          )}
          {bottomTab === 'pnl'       && <PnlChart history={history} theme={theme} />}
        </div>
      </div>

      {showRiskModal && (
        <RiskConfigModal
          rules={riskRules}
          token={token}
          onClose={() => setShowRiskModal(false)}
          onSaved={(saved) => setRiskRules(prev => ({ ...prev, ...saved }))}
        />
      )}
    </div>
  );
};

export default PaperTrading;
